import { api } from './client';

// Types
export type TaskType = 'INDOOR' | 'OUTDOOR' | 'PERSONAL';
export type TaskStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';

export interface Task {
  id: string;
  clientId: number | null;
  client?: {
    id: number;
    name: string;
    address?: string;
  };
  employeeId: string;
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  type: TaskType;
  title: string;
  description: string | null;
  deadline: string | null;
  status: TaskStatus;
  completedAt: string | null;
  createdAt: string;
}

export interface CreateTaskDto {
  clientId?: number;
  type: TaskType;
  title: string;
  description?: string;
  deadline?: string;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  deadline?: string;
  status?: TaskStatus;
}

export interface OutdoorTasksForVisitsResponse {
  byClientId: Record<number, Task[]>;
  personal: Task[];
  total: number;
}

export const tasksApi = {
  // Get all tasks with filters
  getTasks: async (params?: {
    type?: TaskType;
    status?: TaskStatus;
    clientId?: number;
    includeCompleted?: boolean;
  }): Promise<Task[]> => {
    const { data } = await api.get('/tasks', {
      params: {
        ...params,
        includeCompleted: params?.includeCompleted ? 'true' : undefined,
      },
    });
    return data;
  },

  // Get OUTDOOR and PERSONAL tasks for main list
  getListTasks: async (ownershipFilter?: string): Promise<Task[]> => {
    try {
      const { data } = await api.get('/tasks/list', {
        params: ownershipFilter ? { ownershipFilter } : undefined,
      });
      return Array.isArray(data) ? data : [];
    } catch {
      // API not implemented yet
      console.warn('Tasks API not available');
      return [];
    }
  },

  // Get OUTDOOR tasks for visits page (grouped by clientId + personal tasks)
  getOutdoorTasksForVisits: async (ownershipFilter?: string): Promise<OutdoorTasksForVisitsResponse> => {
    try {
      const { data } = await api.get('/tasks/outdoor-for-visits', {
        params: ownershipFilter ? { ownershipFilter } : undefined,
      });
      return data;
    } catch {
      console.warn('Tasks outdoor-for-visits API not available');
      return { byClientId: {}, personal: [], total: 0 };
    }
  },

  // Get INDOOR tasks for client card
  getClientTasks: async (clientId: number): Promise<Task[]> => {
    const { data } = await api.get(`/tasks/client/${clientId}`);
    return data;
  },

  // Get task by ID
  getTask: async (id: string): Promise<Task> => {
    const { data } = await api.get(`/tasks/${id}`);
    return data;
  },

  // Create task
  createTask: async (dto: CreateTaskDto): Promise<Task> => {
    const { data } = await api.post('/tasks', dto);
    return data;
  },

  // Update task
  updateTask: async (id: string, updates: UpdateTaskDto): Promise<Task> => {
    const { data } = await api.patch(`/tasks/${id}`, updates);
    return data;
  },

  // Cancel task
  cancelTask: async (id: string): Promise<Task> => {
    const { data } = await api.post(`/tasks/${id}/cancel`);
    return data;
  },

  // Complete task
  completeTask: async (id: string): Promise<Task> => {
    const { data } = await api.post(`/tasks/${id}/complete`);
    return data;
  },
};
